import {
  ArrowLeft, ArrowLeftRight, Captions, Grid3x3, LogOut, Mic, MicOff, Pause, Phone, PhoneForwarded,
  PhoneIncoming, PhoneOff, Play, Repeat, Send, UserPlus, Users, Voicemail, X,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { briefingLocks, callRoomIds } from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { canDropVoicemail, voicemailOrder } from "@/lib/dialer";
import { closerName } from "@/lib/handoff";
import {
  useDropVoicemail, useHandoffClosers, useLeadCard, useSendToCloser, useVoicemailMessages,
} from "@/lib/queries";
import { directRoomCallId, listedRoomCallId, transferRoom } from "@/lib/rooms";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import {
  rememberConsult, SendToCloserForm, useAcdCall, useAnsweringItself, useBriefing, useConsultOut, useJoiningListenIn,
  useMonitors,
} from "@/softphone/acd-call";
import {
  hasTransferOptions, isEngaged, offered, SHOW_TRANSFER_TO_NUMBER, transferOptions,
} from "@/softphone/call-control";
import type { ConferenceState } from "@/softphone/conference";
import { DialTargetPicker } from "@/softphone/dial-target";
import { BringColleagueSection, useCallerHold, useDirectRoom } from "@/softphone/direct-call";
import { Keypad } from "@/softphone/dialler";
import { participantLocks, useParticipant } from "@/softphone/participant";
import { endCallLabel } from "@/lib/phone-page";
import { useCallTranscription } from "@/softphone/transcription-widget";
import { useCallDuration, useSoftphone, type Call } from "@/softphone/softphone-context";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

/**
 * The parts of a call the phone shows wherever it shows one: the words for a party and a state,
 * the five controls and End call, a second call ringing over the first, a parked leg, and the
 * views pushed over a call — the keypad and Transfer.
 *
 * Shared by the dock (`softphone-dock.tsx`, the 320px phone beside every screen) and the Phone
 * page (`routes/phone.tsx`, the same phone at full size). One implementation, two sizes: a rule
 * about what a control may do lives here once, and the two surfaces cannot disagree about it.
 */

/**
 * What a call control opened over the call, closed with Back or Escape: the keypad in place of
 * the call's own panel, Transfer and Add participant as dialogs over the whole console.
 */
export type SubView = "keypad" | "transfer" | "add";

/** What an internal transfer is called wherever the phone names a call (`isInternalTransfer`). */
export const INTERNAL_TRANSFER = "Internal transfer";

/**
 * Who a call is with, as two lines.
 *
 * The room leg's caller id is the rep's own number, which on a list of calls reads as a
 * stranger, so that leg is named after the room instead. Everyone else is a name over a number
 * when the switch sent a name, and a number over the direction when it did not.
 *
 * An internal transfer is named for what it is, over GoHighLevel's number: the caller id is GHL's
 * line, so no name on it, or on a lead found by it, is the client's. Slack's call messages say the
 * same for the same callers (ADR-042 in `platform`).
 *
 * A call in a room does not say how many are on it here: under the lead's name, beside their
 * number, "2 people on the call" was on every campaign call and told the rep nothing. The Phone
 * page's "On this call" button carries the count, and the dock lists the people. The count still
 * picks the mark (`isRoom`, from three people up). `onCall` is the server's count
 * (`useRoomPeople`), which never counts a supervisor listening in; without it, the switch's
 * roster is counted. A call with no name is its number alone, with no second line.
 */
export function describeParty(
  call: Call,
  conference: ConferenceState | null,
  onCall: number | null = null,
): { title: string; subtitle: string; isRoom: boolean; internalTransfer: boolean } {
  const internalTransfer = call.internalTransfer === true;
  const name = internalTransfer ? INTERNAL_TRANSFER : call.remoteName;
  if (conference && listedRoomCallId(conference) === call.id) {
    // A call on a rep's own number (ADR-032), or one the ACD connected (ADR-041): a call, not a
    // meeting. The leg's caller id is the outside party's, on the rep's leg and on a colleague's
    // alike, so it names the call; the room's name would name nothing a rep recognises.
    const n = onCall ?? conference.participants.length;
    const number = formatPhoneNumber(call.remote);
    return name
      ? { title: name, subtitle: number, isRoom: n > 2, internalTransfer }
      : { title: number, subtitle: "", isRoom: n > 2, internalTransfer };
  }
  if (conference && conference.callId === call.id) {
    const n = conference.participants.length;
    return {
      title: conference.name,
      subtitle: n > 0 ? `Your room · ${n === 1 ? "1 person" : `${n} people`}` : "Your conference room",
      isRoom: true,
      internalTransfer: false,
    };
  }
  const number = formatPhoneNumber(call.remote);
  return name
    ? { title: name, subtitle: number, isRoom: false, internalTransfer }
    : {
        title: number,
        subtitle: call.direction === "inbound" ? "Inbound" : "Outbound",
        isRoom: false,
        internalTransfer,
      };
}

/**
 * The mark of a call ringing: a phone, or for an internal transfer the transfer arrows on the
 * accent colour, so the rep sees which it is before reading a word. Slack marks the same callers
 * with the same arrows.
 */
export function RingingMark({ internalTransfer, small = false }: { internalTransfer: boolean; small?: boolean }) {
  const Icon = internalTransfer ? Repeat : PhoneIncoming;
  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full text-white",
        small ? "size-8" : "size-14",
        internalTransfer ? "bg-accent-9 [--pulse-ring:var(--color-accent-9)]" : "bg-success-9",
      )}
    >
      <Icon className={small ? "size-4" : "size-6"} aria-hidden />
      <span className="animate-pulse-ring" aria-hidden />
    </span>
  );
}

export function partyName(call: Call, conference: ConferenceState | null): string {
  return describeParty(call, conference).title;
}

/** `callerHeld`: the ACD holds the caller on a call in its own room, whose leg to the rep stays up.
 *  `answering`: the call is ringing in and the phone is picking it up by itself. */
export function stateText(
  call: Call, transferring: boolean, callerHeld = false, answering = false,
): string {
  if (transferring) return "Transferring…";
  if (callerHeld) return "Caller on hold";
  switch (call.state) {
    case "trying":
      return "Calling…";
    case "ringing":
      if (call.direction !== "inbound") return "Ringing…";
      return answering ? "Connecting…" : "Incoming";
    case "held":
      return "On hold";
    case "active":
      return "Connected";
    default:
      return "Ended";
  }
}


/** Whether the keyboard is busy somewhere the phone must not pull it away from. */
export function typingElsewhere(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLElement &&
    (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)
  );
}


/**
 * A leg the rep is not on — parked while they talk to someone else.
 *
 * One control: Swap, which is what a parked call is for. Everything else a rep might do with
 * the person on hold — hand them to the room, connect them to the other caller, hang up on
 * them — is done from the call view after swapping, or from Transfer, which lists everyone.
 * The old row carried four icons, and a hang-up glyph beside the wrong name is a dropped lead.
 */
export function ParkedRow({ call }: { call: Call }) {
  const phone = useSoftphone();
  const seconds = useCallDuration(call.answeredAt);
  const { title } = describeParty(call, phone.conference);
  const transferring = phone.handingOver.includes(call.id);
  // Says what is true rather than what is usual: only the switch's own reply makes a call "on
  // hold", and a parked leg that is not held is the one state a rep needs to see at once.
  const held = call.state === "held";
  return (
    <li className="flex items-center gap-2.5 rounded-lg border border-border bg-surface-sunken py-2 pl-3 pr-2">
      <Pause className="size-4 shrink-0 text-warning-11" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-text">{title}</p>
        <p className={cn("text-xs", held ? "text-text-muted" : "text-danger-11")}>
          {transferring ? "Transferring…" : held ? "On hold" : "Connected, not on hold"}
          {isEngaged(call.state) && (
            <>
              {" · "}
              <span className="font-mono tabular">{formatDuration(seconds)}</span>
            </>
          )}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        disabled={transferring}
        onClick={() => void phone.switchTo(call.id)}
        aria-label={`Swap to ${title}`}
      >
        <Repeat />
        Swap
      </Button>
    </li>
  );
}

/**
 * A second call, ringing while the rep is already on one.
 *
 * With `decision` set there are three choices and no default, because there is no safe default:
 * answering silently would leave the first party live on an open microphone, and letting it
 * ring out loses a lead. "Hold & answer" parks the first party properly — only one leg is ever
 * off hold — and "End & answer" is the honest version of hanging up on them. Without it (the
 * rep's own call is still dialling, so there is no conversation to protect) it is a plain
 * Answer or Decline.
 */
export function IncomingBanner({ call, decision }: { call: Call; decision: boolean }) {
  const phone = useSoftphone();
  const { title, internalTransfer } = describeParty(call, phone.conference);
  const [busy, setBusy] = React.useState<null | "hold" | "end" | "answer">(null);
  // The supervisor moving to another rep's call: the phone leaves the one they were on and joins
  // this one by itself, so there is no choice to offer.
  const joining = useJoiningListenIn(call.id);
  const monitor = useMonitors().get(call.id);
  if (joining) {
    return (
      <div
        className="flex items-center gap-2.5 rounded-lg border border-accent-8 bg-surface-sunken p-2.5"
        role="status"
      >
        <Spinner className="size-4 shrink-0 text-accent-11" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text">Connecting to call session</p>
          <p className="truncate text-xs text-text-muted">
            Please wait{monitor ? `. You are joining ${monitor.repName}'s call.` : "."}
          </p>
        </div>
      </div>
    );
  }

  const act = async (kind: "hold" | "end" | "answer") => {
    setBusy(kind);
    try {
      if (kind === "hold") await phone.holdAndAnswer(call.id);
      else if (kind === "end") await phone.endAndAnswer(call.id);
      else await phone.answer(call.id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-lg border border-success-9 bg-success-2 p-2.5"
      role="group"
      aria-label={`Incoming call from ${title}`}
    >
      <div className="flex items-center gap-2.5">
        <RingingMark internalTransfer={internalTransfer} small />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text">{title}</p>
          <p className="text-xs text-success-11">Incoming call</p>
        </div>
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5">
        {decision ? (
          <>
            <Button
              variant="default"
              className="h-10 bg-success-9 text-sm hover:brightness-110"
              loading={busy === "hold"}
              disabled={busy !== null}
              onClick={() => void act("hold")}
            >
              <Pause />
              Hold &amp; answer
            </Button>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                variant="outline"
                className="h-9"
                loading={busy === "end"}
                disabled={busy !== null}
                onClick={() => void act("end")}
              >
                <PhoneOff />
                End &amp; answer
              </Button>
              <Button
                variant="outline"
                className="h-9 text-danger-11"
                disabled={busy !== null}
                onClick={() => void phone.reject(call.id)}
              >
                <X />
                Decline
              </Button>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            <Button
              variant="outline"
              className="h-10 text-danger-11"
              disabled={busy !== null}
              onClick={() => void phone.reject(call.id)}
            >
              <X />
              Decline
            </Button>
            <Button
              variant="default"
              className="h-10 bg-success-9 text-sm hover:brightness-110"
              loading={busy === "answer"}
              disabled={busy !== null}
              onClick={() => void act("answer")}
            >
              <Phone />
              Answer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One of the six call controls as a fact about the call, not yet a button: what it is called,
 * whether it is on, whether it applies right now and why not. The dock draws each as a tile in a
 * fixed grid, the Phone page as a chip in a row, and both read the same list, so a rule about
 * when Hold applies, or what Hold does on a call that lives in a room, is decided once.
 */
export interface CallControlSpec {
  id: SubView | "mute" | "hold" | "transcribe";
  icon: LucideIcon;
  label: string;
  on?: boolean;
  disabled?: boolean;
  loading?: boolean;
  hint?: string;
  onClick: () => void;
}

/**
 * The six controls for the call the rep is on, in their fixed order: Mute · Hold · Keypad ·
 * Add participant · Transfer · Transcribe. A control that does not apply is dimmed with a
 * reason, never dropped, so the row does not rearrange under a reaching hand. There is no Add
 * call: Transfer brings a colleague in, talks to them first, or sends the caller on, which is
 * what a second call was for; Add participant rings an outside number into the call (ADR-065).
 *
 * Hold is three different things underneath, and the tile hides that on purpose: on a call in a
 * room the ACD made it goes through the ACD, on a call on the rep's own number (ADR-032) it holds
 * the caller in the room through core-api, and only on a plain bridged call is it the phone's own
 * hold. Holding the rep's own leg on a room call would leave the caller hearing nobody.
 */
export function useCallControls(
  call: Call,
  onPush: (view: SubView) => void,
): { controls: CallControlSpec[]; up: boolean; held: boolean } {
  const phone = useSoftphone();
  const { user } = useAuth();
  // Shared with the transcript panel: this control flips it, the panel renders it
  // (transcription-widget.tsx owns the state, above both).
  const transcription = useCallTranscription();
  const up = isEngaged(call.state);
  const acd = useAcdCall();
  const direct = directRoomCallId(phone.conference) === call.id;
  // One rule for all three holds, shared with the roster row (`useCallerHold`).
  const hold = useCallerHold(call);
  const held = hold.held;
  // A briefing holds the client itself and refuses the tile (`briefingLocks`).
  const briefing = useBriefing();
  const locks = briefingLocks(briefing, acd, call.id);
  // An outside participant being rung or briefed does the same (ADR-065).
  const participant = useParticipant(call.id);
  const outside = participantLocks(participant);
  const inRoom = listedRoomCallId(phone.conference) === call.id;
  // A call the ACD connected can go to a closer too, which is a section of Transfer; a direct
  // call has a colleague to bring in or send the caller to.
  const canTransfer =
    hasTransferOptions(
      offered(
        transferOptions(
          phone.calls, phone.activeCallId, transferRoom(phone.conference), callRoomIds(acd),
        ),
      ),
    ) || acd?.state === "busy" || (direct && up);
  // A supervisor's listen-in (ADR-029) has nobody of theirs to hold or hand on: the call is the
  // rep's, and holding their own leg in its room or transferring it means nothing to the client.
  const supervising = useMonitors().has(call.id);
  const controls: CallControlSpec[] = [
    {
      id: "mute", icon: call.muted ? MicOff : Mic, label: "Mute", on: call.muted,
      onClick: () => void phone.toggleMute(call.id),
    },
    {
      id: "hold", icon: held ? Play : Pause, label: "Hold", on: held,
      loading: hold.pending,
      disabled: !up || locks !== null || outside !== null,
      hint: up ? (locks?.hold ?? outside?.hold) : "Once the call connects",
      onClick: hold.toggle,
    },
    {
      id: "keypad", icon: Grid3x3, label: "Keypad", disabled: call.state !== "active",
      hint:
        call.state === "active"
          ? undefined
          : call.state === "held"
            ? "Resume the call first"
            : "Once the call connects",
      onClick: () => onPush("keypad"),
    },
    {
      id: "add", icon: UserPlus, label: "Add participant",
      disabled: !up || !inRoom || outside !== null || briefing !== null,
      hint: !up
        ? "Once the call connects"
        : !inRoom
          ? seesCampaigns(user?.role)
            ? "Only on campaign calls and calls on your own number"
            : "Not on this call"
          : (outside?.add ?? (briefing ? "Finish the briefing first" : undefined)),
      onClick: () => onPush("add"),
    },
    {
      id: "transfer", icon: PhoneForwarded, label: "Transfer",
      disabled: !canTransfer || outside !== null,
      // "Nowhere to send this call", not "No caller to transfer": with the typed destination
      // hidden, a rep on a plain call they dialled themselves has a caller and no hand-over for
      // them, and the old wording denied the person they were talking to.
      hint: outside?.transfer ?? (canTransfer ? undefined : up ? "Nowhere to send this call" : "Once the call connects"),
      onClick: () => onPush("transfer"),
    },
    {
      id: "transcribe", icon: Captions, label: "Transcribe", on: transcription.on,
      loading: transcription.pending,
      // Not before the call is up: the leg exists while it rings, but the tap would record
      // ringback, and the far side has said nothing to transcribe.
      disabled: !up, hint: up ? undefined : "Once the call connects",
      onClick: transcription.toggle,
    },
  ];
  return {
    controls: supervising ? controls.filter((c) => !LISTEN_IN_HIDES.has(c.id)) : controls,
    up,
    held,
  };
}

/** The controls a supervisor's listen-in does not show: Stop supervising is their only way off it,
 *  and an outside participant is the rep's to bring in. */
const LISTEN_IN_HIDES = new Set(["hold", "transfer", "add"]);

/** The six controls drawn: tiles in the dock, three over three, and a row of chips on the Phone
 *  page. A listen-in's three sit in one row. */
export function CallControls({
  call, onPush, layout = "grid",
}: {
  call: Call;
  onPush: (view: SubView) => void;
  layout?: "grid" | "row";
}) {
  const { controls } = useCallControls(call, onPush);
  return (
    <div
      className={layout === "row" ? "flex flex-wrap items-center gap-1.5" : "grid grid-cols-6 gap-1.5"}
      role="group"
      aria-label="Call controls"
    >
      {controls.map((control) => (
        <Tile
          key={control.id}
          {...control}
          shape={layout === "row" ? "chip" : "tile"}
          className={layout === "row" ? undefined : "col-span-2"}
        />
      ))}
    </div>
  );
}

/**
 * One of the six call controls: an icon over a word, in a fixed place.
 *
 * Toggles (mute, hold, transcribe) report `aria-pressed` and keep their label — "Mute" pressed,
 * not a button that reads "Unmute" — because a label that changes under the pointer is the
 * classic way to click the wrong thing twice. A control that does not apply is `aria-disabled`
 * rather than `disabled`: it stays in the tab order and keeps its tooltip, so the reason it is
 * dimmed is one Tab away for everyone, not only on hover for people with a mouse.
 *
 * The tooltip is for that reason and nothing else. An enabled tile already says what it is, and
 * a tooltip that opened every time focus landed on it — which it does, on the way back from a
 * sub-view — would be a popup over the controls the rep is about to use.
 */
export function Tile({
  id, icon: Icon, label, on, disabled = false, loading = false, hint, onClick, shape = "tile", className,
}: CallControlSpec & {
  /** A tile is an icon over a word, for the dock's grid; a chip an icon beside it, for a row. */
  shape?: "tile" | "chip";
  className?: string;
}) {
  const blocked = disabled || loading;
  const button = (
    <button
      type="button"
      data-tile={id}
      aria-pressed={on}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      onClick={() => {
        if (!blocked) onClick();
      }}
      className={cn(
        shape === "chip"
          ? "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium"
          : "flex h-14 flex-col items-center justify-center gap-1 rounded-lg border text-xs font-medium",
        "transition-colors",
        className,
        blocked
          ? // A control that is ON and cannot be pressed keeps its colour, dimmed: Hold during a
            // briefing is the truth about the call, and a grey tile would deny it.
            on
            ? "cursor-not-allowed border-accent-6 bg-accent-3 text-accent-11 opacity-70"
            : "cursor-not-allowed border-border bg-surface-sunken text-text-subtle"
          : on
            ? "border-accent-6 bg-accent-3 text-accent-11 hover:bg-accent-4"
            : "border-border bg-surface text-text hover:bg-surface-hover active:bg-gray-4",
      )}
    >
      {loading ? <Spinner /> : <Icon className="size-[18px]" aria-hidden />}
      {label}
    </button>
  );
  return hint ? <Tooltip content={hint}>{button}</Tooltip> : button;
}

export function EndCallButton({ call, className }: { call: Call; className?: string }) {
  const phone = useSoftphone();
  const up = isEngaged(call.state);
  // On a call on the rep's own number, hanging up with two others on it leaves them talking
  // (ADR-032), and the button says so instead of promising to end the call.
  const { isDirect, room } = useDirectRoom(call.id);
  const others = isDirect ? (room?.members.filter((m) => !m.is_self).length ?? 0) : 0;
  // A supervisor listening in, whispering or barging leaves the rep's call, which goes on: not a
  // red hang-up, and it says "Stop supervising".
  const supervising = useMonitors().has(call.id);
  // A call the phone is picking up by itself is up within a moment: the button says what it will
  // say for the rest of the call from its first frame, instead of changing under the pointer.
  const answering = useAnsweringItself(
    call.direction === "inbound" && call.state === "ringing" ? call.id : null,
  );
  const label = endCallLabel(up || answering, others, supervising);
  // While the setter is briefing a closer, hanging up ends nothing — the ACD hands the client
  // over — so the button waits for one of the three endings instead (`briefingLocks`).
  const acd = useAcdCall();
  const briefing = useBriefing();
  const blocked = briefingLocks(briefing, acd, call.id)?.endCall ?? null;
  // `aria-disabled`, not `disabled`, for the same reason a dimmed tile keeps its place: the
  // button stays in the tab order and keeps the tooltip that says why it is dimmed.
  const button = (
    <Button
      variant={supervising ? "outline" : "danger"}
      className={cn(
        "h-11 w-full text-sm",
        blocked !== null && "cursor-not-allowed opacity-50 hover:brightness-100",
        className,
      )}
      aria-disabled={blocked !== null || undefined}
      title={
        supervising
          ? "The rep's call goes on without you"
          : blocked === null && label === "Leave the call"
            ? "The others stay on the call"
            : undefined
      }
      onClick={() => {
        if (blocked === null) void phone.hangup(call.id);
      }}
    >
      {supervising ? <LogOut /> : <PhoneOff />}
      {label}
    </Button>
  );
  return blocked === null ? button : <Tooltip content={blocked}>{button}</Tooltip>;
}


/**
 * A supervisor's listen-in on its way in: the phone answers it by itself (`useMonitorAutoAnswer`),
 * so there is nothing to answer or decline, and the screen says it is connecting until the call
 * view with Listen, Whisper and Barge in takes over. Stop supervising is the way out when joining
 * fails (a refused microphone, say).
 */
export function ListenInConnecting({ call }: { call: Call }) {
  const monitor = useMonitors().get(call.id);
  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-accent-3 text-accent-11">
        <Spinner className="size-6" />
      </span>
      <div className="w-full min-w-0" role="status">
        <p className="text-lg font-semibold text-text">Connecting to call session</p>
        <p className="truncate text-xs text-text-muted">
          Please wait{monitor ? `. You are joining ${monitor.repName}'s call.` : "."}
        </p>
      </div>
      <EndCallButton call={call} />
    </div>
  );
}

/** Focus the element on mount — a heading, so a reader announces where it has arrived. */
export function useFocusOnMount<T extends HTMLElement>(enabled = true) {
  const ref = React.useRef<T>(null);
  React.useEffect(() => {
    if (enabled) ref.current?.focus();
    // On mount only: the point is where the keyboard lands when the view appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

export function SubHeader({
  title, subtitle, onBack, focusHeading = true,
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
  /** Off where the view has a field the rep came to type into, which takes focus instead. */
  focusHeading?: boolean;
}) {
  const heading = useFocusOnMount<HTMLHeadingElement>(focusHeading);
  return (
    <div className="flex items-start gap-1.5">
      <Button
        variant="ghost"
        size="icon"
        className="-ml-1.5 shrink-0"
        onClick={onBack}
        aria-label="Back to the call"
      >
        <ArrowLeft />
      </Button>
      <div className="min-w-0 flex-1 pt-1">
        <h3 ref={heading} tabIndex={-1} className="text-sm font-semibold text-text outline-none">
          {title}
        </h3>
        {subtitle && <p className="text-xs text-text-muted">{subtitle}</p>}
      </div>
    </div>
  );
}

/**
 * The in-call keypad. Digits go down the line as DTMF, not into a text field — this is for an
 * IVR, not for dialling. What was sent is echoed above the keys, the way a handset shows it, so
 * a rep who lost count of an account number can see it.
 */
export function KeypadView({
  call, onBack, withEndCall = true,
}: {
  call: Call;
  onBack: () => void;
  /** Off where End call is already on screen beside the keypad, as on the Phone page. */
  withEndCall?: boolean;
}) {
  const phone = useSoftphone();
  const { title } = describeParty(call, phone.conference);
  const [sent, setSent] = React.useState("");

  const press = (digit: string) => {
    setSent((s) => (s + digit).slice(-24));
    void phone.sendDigit(digit, call.id);
  };

  return (
    <div className="flex flex-col gap-3">
      <SubHeader title="Keypad" subtitle={`Tones go to ${title}`} onBack={onBack} />
      <output
        aria-label="Digits sent"
        className="flex h-10 items-center justify-center rounded-md bg-surface-sunken px-3 font-mono text-base tracking-[0.2em] tabular text-text"
      >
        {sent || (
          <span className="text-sm tracking-normal text-text-subtle">Nothing sent yet</span>
        )}
      </output>
      <Keypad onPress={press} tall />
      {withEndCall && <EndCallButton call={call} />}
    </div>
  );
}

/**
 * Every hand-over the phone can make right now, described by the people involved.
 *
 * A dialog rather than a panel pushed over the call, and centred rather than the right-hand
 * drawer the rest of the console edits in: a transfer is made with a caller waiting on the line,
 * so the call it is about stays on screen behind it, and the one decision the rep came here to
 * make sits in the middle of the screen rather than at its edge. Both phones open the same one —
 * the dock is 320px wide, which this list never fitted, and on the Phone page it used to replace
 * the panel naming the person being handed over.
 *
 * Nothing here renders a success state, because there is none to render: each of these ends
 * the rep's call with the person handed over, and that leg disappearing IS the transfer. So a
 * request that was accepted closes the dialog, and the party in question reads "Transferring…"
 * on the call behind it until the switch has moved them (`handingOver`). A request that failed
 * keeps the dialog open with the reason, and the caller is still on the line.
 */
export function TransferDialog({
  open, colleague = null, onClose,
}: {
  open: boolean;
  /** The colleague picked when it opens: a row of the Phone page's Colleagues card. */
  colleague?: string | null;
  onClose: () => void;
}) {
  const title = React.useRef<HTMLHeadingElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        size="lg"
        // On the heading, as the panel this replaced put it. Left to itself the dialog takes the
        // first thing that can be tabbed to, which here is the Needs chip: a rep who opened
        // Transfer from the keyboard and pressed Space would have narrowed the closers by a skill
        // without ever meaning to.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          title.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle ref={title} tabIndex={-1} className="outline-none">
            Transfer
          </DialogTitle>
          <DialogDescription>
            Your call with whoever you hand over ends once they are connected.
          </DialogDescription>
        </DialogHeader>
        {/* Keyed so a second row's Bring in, with the dialog already open, picks that one. The
            dialog holds no contents while it is closed, so every transfer starts on a clean form
            and the closers are read while there is somebody to hand over, never before. */}
        <TransferOptions key={colleague ?? ""} colleague={colleague} onDone={onClose} />
      </DialogContent>
    </Dialog>
  );
}

function TransferOptions({
  colleague, onDone,
}: {
  colleague: string | null;
  onDone: () => void;
}) {
  const phone = useSoftphone();
  const toast = useToast();
  const acd = useAcdCall();
  // A call in its own room (ADR-029) goes to a closer or a number, and to nothing that moves a
  // bridge partner (`transferOptions`).
  const options = offered(
    transferOptions(
      phone.calls, phone.activeCallId, transferRoom(phone.conference), callRoomIds(acd),
    ),
  );
  const sendToCloser = useSendToCloser();
  const closer = acd?.state === "busy" ? acd : null;
  // A caller already on their way to one (ADR-058): the call screen follows that request, and
  // taking it back there is how the rep changes it.
  const going = useConsultOut(closer?.callerChannelId ?? null);
  // Who the caller can go to (ADR-031): read only while a closer can be picked.
  const closerList = useHandoffClosers(closer?.callerChannelId ?? null, {
    enabled: closer !== null,
  });
  // A lead the dialer reached, whose voicemail greeting the rep may be listening to.
  const voicemail = canDropVoicemail(acd) ? acd : null;
  const messages = useVoicemailMessages(voicemail !== null);
  // The lead's own message (ADR-061). The same query the lead card already runs for this call,
  // so it is served from cache rather than fetched again.
  const card = useLeadCard(
    voicemail !== null ? (acd?.leadId ?? null) : null,
    acd?.sessionId ?? acd?.callerChannelId ?? null,
  ).data;
  const suggested = card?.suggested_voicemail ?? null;
  const dropVoicemail = useDropVoicemail();
  const [error, setError] = React.useState<string | null>(null);
  const name = (call: Call) => partyName(call, phone.conference);
  const busy = (call: Call) => phone.handingOver.includes(call.id);
  // A call on the rep's own number (ADR-032): a colleague to bring in or to send the caller to,
  // and a number to send them to. Its leg is a room member, so `transferOptions` lists it under
  // nothing above; these are its own sections.
  const directId = directRoomCallId(phone.conference);
  const direct = phone.calls.find((c) => c.id === directId && isEngaged(c.state)) ?? null;

  const attempt = async (request: () => Promise<unknown>, fallback: string) => {
    setError(null);
    try {
      await request();
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    }
  };

  const people = phone.conference
    ? `${phone.conference.participants.length === 1 ? "1 person" : `${phone.conference.participants.length} people`} there`
    : "";

  return (
    <DialogBody className="flex flex-col gap-3">
      {/* Above the sections rather than below them: a refusal the rep has to scroll to find is
          one they read after they have decided the transfer went through. */}
      {error && <Callout tone="danger">{error}</Callout>}

      {closer && going && (
        <Section label="To a closer">
          <p className="text-xs text-text-muted">
            This caller is already on their way to a closer. The call screen follows it — take it
            back there if you want to send them somewhere else.
          </p>
        </Section>
      )}

      {closer && !going && (
        <Section label="To a closer">
          <SendToCloserForm
            busy={sendToCloser.isPending}
            closers={closerList.data}
            loadingClosers={closerList.isPending}
            onSend={(note, mode, choice) =>
              void attempt(async () => {
                const handoff = await sendToCloser.mutateAsync({
                  callId: closer.callerChannelId,
                  note,
                  mode,
                  toSipEndpointId: choice.target?.sip_endpoint_id ?? null,
                  requiredSkills: choice.needs,
                });
                const who = choice.target ? closerName(choice.target) : null;
                if (mode === "consult") {
                  // The caller notices nothing until a closer picks up: the bar on the call
                  // screen follows it from here (ADR-027).
                  rememberConsult(handoff.id, closer.callerChannelId);
                  toast.success(
                    who ? `Trying ${who}` : "Looking for a closer",
                    "Stay on the call — we will tell you.",
                  );
                } else {
                  toast.success(
                    who ? `Sent to ${who}` : "Sent to a closer",
                    who
                      ? `They wait on hold for ${who} first.`
                      : "They wait on hold for the next closer free.",
                  );
                }
              }, "Could not send the caller to a closer.")
            }
          />
        </Section>
      )}

      {voicemail && (
        <Section label="To a voicemail message">
          {messages.isPending ? (
            <Spinner className="size-4" />
          ) : (messages.data ?? []).length === 0 ? (
            <p className="text-xs text-text-muted">
              No voicemail messages yet. An admin adds them to the library.
            </p>
          ) : (
            voicemailOrder(messages.data ?? [], suggested).map((message) => (
              <OptionRow
                key={message.id}
                icon={Voicemail}
                title={`Leave “${message.name}”`}
                subtitle={`${message.slug === suggested ? "Suggested for this lead · " : ""}${message.language ? `${message.language.toUpperCase()} · ` : ""}It plays to their voicemail, and your call ends.`}
                loading={dropVoicemail.isPending && dropVoicemail.variables?.template === message.slug}
                onClick={() =>
                  void attempt(async () => {
                    await dropVoicemail.mutateAsync({
                      callId: voicemail.callerChannelId,
                      template: message.slug,
                    });
                    toast.success("Leaving a voicemail", message.name);
                  }, "Could not leave the message.")
                }
              />
            ))
          )}
        </Section>
      )}

      {direct && (
        <Section label="Bring a colleague in, or send the caller to one">
          <BringColleagueSection callId={direct.id} initialPick={colleague} onDone={onDone} />
        </Section>
      )}

      {SHOW_TRANSFER_TO_NUMBER && direct && (
        <Section label="Send the caller to a number">
          <BlindTransferForm
            call={direct}
            onSend={(destination) =>
              attempt(
                () => phone.blindTransfer(direct.id, destination),
                "Could not send the caller on. Check the number.",
              )
            }
          />
        </Section>
      )}

      {options.toRoom.length > 0 && phone.conference && (
        <Section label="Into your room">
          {options.toRoom.map((c) => (
            <OptionRow
              key={c.id}
              icon={Users}
              title={`Move ${name(c)} into ${phone.conference?.name}`}
              subtitle={`${people} · you stay in the room`}
              loading={busy(c)}
              onClick={() =>
                void attempt(
                  () => phone.addToRoom(c.id),
                  "Could not move this caller into your room.",
                )
              }
            />
          ))}
        </Section>
      )}

      {options.pair && (
        <Section label="To each other">
          <OptionRow
            icon={ArrowLeftRight}
            title={`Connect ${name(options.pair[0])} with ${name(options.pair[1])}`}
            subtitle="They talk directly. You leave both calls."
            loading={busy(options.pair[0]) || busy(options.pair[1])}
            onClick={() => {
              const [from, to] = options.pair as [Call, Call];
              void attempt(
                () => phone.warmTransfer(from.id, to.id),
                "Could not connect these two calls.",
              );
            }}
          />
        </Section>
      )}

      {options.toNumber.length > 0 && (
        <Section label="To a number or extension">
          {options.toNumber.map((c) => (
            <BlindTransferForm
              key={c.id}
              call={c}
              onSend={(destination) =>
                attempt(
                  () => phone.blindTransfer(c.id, destination),
                  "Could not transfer this call. Check the number.",
                )
              }
            />
          ))}
        </Section>
      )}

      {!hasTransferOptions(options) && !closer && !direct && (
        <p className="text-xs text-text-muted">
          Nothing to transfer right now — a call has to be connected first.
        </p>
      )}
    </DialogBody>
  );
}

export function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-text-muted" aria-hidden>
        {label}
      </p>
      {children}
    </div>
  );
}

export function OptionRow({
  icon: Icon, title, subtitle, loading, onClick,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-busy={loading || undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left",
        "transition-colors hover:bg-surface-hover disabled:cursor-wait disabled:opacity-60",
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-3 text-accent-11">
        {loading ? <Spinner /> : <Icon className="size-4" aria-hidden />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug text-text">{title}</span>
        <span className="block text-xs text-text-muted">{subtitle}</span>
      </span>
    </button>
  );
}

/**
 * Where a caller is being sent. The destination is not validated beyond "they typed something":
 * the server refuses what it cannot make safe, and a second copy of that rule in the browser
 * would be the copy that goes stale. Enter sends it; a rep who has typed an extension has
 * already decided.
 *
 * The colleague picker is the same one the dialler carries, and this is the surface it matters
 * most on: a transfer is nearly always to a person, and it is the one moment a rep is holding a
 * live caller while trying to remember four digits. Picking somebody sends the caller straight
 * to them — the same "one intention, one act" rule the dialler follows.
 */
export function BlindTransferForm({
  call, onSend,
}: {
  call: Call;
  onSend: (destination: string) => Promise<void>;
}) {
  const phone = useSoftphone();
  const id = React.useId();
  const [destination, setDestination] = React.useState("");
  const busy = phone.handingOver.includes(call.id);
  const { title } = describeParty(call, phone.conference);

  return (
    <form
      className="flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        const typed = destination.trim();
        if (typed && !busy) void onSend(typed);
      }}
    >
      <label htmlFor={id} className="text-xs text-text-muted">
        Send {title} to
      </label>
      <div className="flex gap-1.5">
        <Input
          id={id}
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder="Extension or number"
          inputMode="tel"
          autoComplete="off"
          className="h-10 font-mono"
        />
        <DialTargetPicker
          label="Transfer to a colleague"
          disabled={busy}
          onPick={(ext) => void onSend(ext)}
        />
        <Button
          type="submit"
          variant="outline"
          className="h-10 shrink-0"
          loading={busy}
          disabled={!destination.trim()}
        >
          <Send />
          Send
        </Button>
      </div>
    </form>
  );
}
